import { Row, Col, Form, Button, Spinner, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { DateInput } from '../../../ui/DateInput/DateInput';
import ModalDialog from '../../../ui/ModalDialog/ModalDialog';
import { useAddChildModal } from './useAddChildModal';
import styles from './AddChildModal.module.css';

interface ChildFormData {
  firstName: string;
  lastName: string;
  middleName: string;
  dateOfBirthUtc: Date | null;
}

interface AddChildModalProps {
  show: boolean;
  onHide: () => void;
  childFormData: ChildFormData;
  childFormErrors: string[];
  addingChild: boolean;
  childFormMode: 'add' | 'edit';
  showDeleteChildConfirmation: boolean;
  deletingChild: boolean;
  onUpdateChildFormData: (key: keyof ChildFormData, value: string | Date | null) => void;
  onAddChild: () => void;
  onShowDeleteConfirmation: () => void;
  onHideDeleteConfirmation: () => void;
  onDeleteChild: () => void;
}

export const AddChildModal = ({
  show,
  onHide,
  childFormData,
  childFormErrors,
  addingChild,
  childFormMode,
  showDeleteChildConfirmation,
  deletingChild,
  onUpdateChildFormData,
  onAddChild,
  onShowDeleteConfirmation,
  onHideDeleteConfirmation,
  onDeleteChild
}: AddChildModalProps) => {
  const manager = useAddChildModal({
    childFormData,
    childFormErrors
  });

  return (
    <>
      {/* Delete Confirmation Modal */}
      <ModalDialog
        showDialog={showDeleteChildConfirmation}
        heading="Delete Child"
        message={`Are you sure you want to delete ${childFormData.firstName} ${childFormData.lastName}? This action cannot be undone.`}
        okCallback={onDeleteChild}
        okButtonText="Yes, delete child"
        cancelCallback={onHideDeleteConfirmation}
        cancelButtonText="Cancel"
      />

      {/* Main Modal */}
      <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{childFormMode === 'edit' ? 'Edit' : 'Add'} Child Family Member</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-2 mb-2">
            <Col md={4}>
              <Form.Group className={styles.compactFormGroup}>
                <Form.Label
                  className={
                    childFormErrors.includes('firstName')
                      ? `${styles.required} is-invalid`
                      : styles.required
                  }>
                  First Name
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={childFormData.firstName}
                  onChange={(e) => onUpdateChildFormData('firstName', e.target.value)}
                  isInvalid={childFormErrors.includes('firstName')}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className={styles.compactFormGroup}>
                <Form.Label>Middle Name</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={childFormData.middleName}
                  onChange={(e) => onUpdateChildFormData('middleName', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className={styles.compactFormGroup}>
                <Form.Label
                  className={
                    childFormErrors.includes('lastName')
                      ? `${styles.required} is-invalid`
                      : styles.required
                  }>
                  Last Name
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={childFormData.lastName}
                  onChange={(e) => onUpdateChildFormData('lastName', e.target.value)}
                  isInvalid={childFormErrors.includes('lastName')}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-2">
            <Col md={4}>
              <DateInput
                label="Birth Date"
                value={childFormData.dateOfBirthUtc}
                onChange={(date) => onUpdateChildFormData('dateOfBirthUtc', date)}
                isInvalid={childFormErrors.includes('dateOfBirthUtc')}
                required
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              {childFormMode === 'edit' && (
                <Button
                  variant="danger"
                  onClick={onShowDeleteConfirmation}
                  disabled={addingChild || deletingChild}>
                  {deletingChild ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete Child
                    </>
                  )}
                </Button>
              )}
            </div>
            <div>
              <Button
                variant="secondary"
                onClick={onHide}
                disabled={addingChild || deletingChild}
                className="me-2">
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={onAddChild}
                disabled={addingChild || deletingChild}>
                {addingChild ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    {childFormMode === 'edit' ? 'Updating...' : 'Adding...'}
                  </>
                ) : childFormMode === 'edit' ? (
                  'Update Child'
                ) : (
                  'Add Child'
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};
